// Encrypted PII vault: AES-GCM via Web Crypto. The per-device key is a non-extractable
// CryptoKey stored in IndexedDB; only ciphertext + IV are ever persisted. Plaintext never
// touches IndexedDB, localStorage, or Zustand persistence.
const DB_NAME = "buildease-secure";
const KEY_STORE = "keys";
const VAULT_STORE = "vault";
const DEVICE_KEY_ID = "device-aes-gcm-v1";

interface VaultRecord {
  iv: ArrayBuffer;
  ct: ArrayBuffer;
  updatedAt: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(KEY_STORE)) db.createObjectStore(KEY_STORE);
      if (!db.objectStoreNames.contains(VAULT_STORE)) db.createObjectStore(VAULT_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function tx<T>(store: string, mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const req = run(db.transaction(store, mode).objectStore(store));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

async function getDeviceKey(): Promise<CryptoKey> {
  const existing = await tx<CryptoKey | undefined>(KEY_STORE, "readonly", (s) => s.get(DEVICE_KEY_ID));
  if (existing) return existing;
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  await tx(KEY_STORE, "readwrite", (s) => s.put(key, DEVICE_KEY_ID));
  return key;
}

export async function putSecret(name: string, value: unknown): Promise<void> {
  const key = await getDeviceKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, new TextEncoder().encode(JSON.stringify(value)));
  const record: VaultRecord = { iv: iv.buffer, ct, updatedAt: new Date().toISOString() };
  await tx(VAULT_STORE, "readwrite", (s) => s.put(record, name));
}

export async function getSecret<T>(name: string): Promise<T | null> {
  const record = await tx<VaultRecord | undefined>(VAULT_STORE, "readonly", (s) => s.get(name));
  if (!record) return null;
  const key = await getDeviceKey();
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: record.iv }, key, record.ct);
  return JSON.parse(new TextDecoder().decode(plain)) as T;
}

export async function hasSecret(name: string): Promise<boolean> {
  return (await tx<number>(VAULT_STORE, "readonly", (s) => s.count(name))) > 0;
}

export async function deleteSecret(name: string): Promise<void> {
  await tx(VAULT_STORE, "readwrite", (s) => s.delete(name));
}

export const SECRETS = {
  identity: "onboarding.identity",
} as const;
